import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/AuthContext";
import { Users, ClipboardList, BarChart3, UserPlus, IndianRupee, ClipboardListIcon, NotepadText, NotebookText } from "lucide-react";
import { useGetDashboardAnalytics } from "../hooks/useDashboardApi";
import Loader from "../components/ui/Loader";

// --- Reusable StatCard ---
const StatCard = ({ title, value, icon, linkTo, inactive = 0 }) => {
  const CardContent = (
    <>
      <div className="flex items-center">
        <div className="flex-shrink-0 bg-[#2C4E86] rounded-md p-3">
          {React.cloneElement(icon, { className: "h-4 w-4 text-white" })}
        </div>
        <div className="ml-2">
          <p className="text-sm font-semibold text-gray-900truncate">{title}</p>
        </div>
      </div>

      {/* Active/Inactive section */}
      <div className="flex flex-col mt-5 space-y-1">
      <div className="flex w-full justify-between items-center">
          <p className="text-sm font-medium text-gray-500 truncate">Total Employee</p>
          <p className=" text-gray-800 bg-gray-200 w-8 h-8 rounded-full flex items-center justify-center">{value?.total}</p>
        </div>
        <div className="flex w-full justify-between items-center">
          <p className="text-sm font-medium text-gray-500 truncate">Present</p>
          <p className=" text-gray-800 bg-gray-200 w-8 h-8 rounded-full flex items-center justify-center">{value?.present}</p>
        </div>
        <div className="flex w-full justify-between items-center">
          <p className="text-sm font-medium text-gray-500 truncate">Leave</p>
          <p className=" text-gray-800 bg-gray-200 w-8 h-8 rounded-full flex items-center justify-center">{value?.leave}</p>
        </div>
      </div>
    </>
  );

  return linkTo ? (
    <Link
      to={linkTo}
      className="block p-6 bg-white rounded-lg shadow hover:bg-gray-50 transition"
    >
      {CardContent}
    </Link>
  ) : (
    <div className="block p-6 bg-white rounded-lg shadow">{CardContent}</div>
  );
};


// --- Reusable GroupedStatCard (whole card clickable) ---
const GroupedStatCard = ({ title, icon, stats = [], linkTo }) => (
  <Link
    to={linkTo}
    className="block p-6 bg-white rounded-lg shadow hover:bg-gray-50 transition"
  >
    <div className="flex items-center">
      <div className="flex-shrink-0 bg-[#2C4E86] rounded-md p-3">
        {React.cloneElement(icon, { className: "h-4 w-4 text-white" })}
      </div>
      <div className="ml-2">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
      </div>
    </div>
    <div className="mt-4 space-y-3">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="flex justify-between items-center text-sm  rounded"
        >
          <span className="text-gray-600">{stat.label}</span>
          <span className=" text-gray-800 bg-gray-200 w-8 h-8 rounded-full flex items-center justify-center">
            {stat.value}
          </span>
        </div>
      ))}
    </div>
  </Link>
);

//---- Function OverView------


// --- QuickActionsPanel ---
const QuickActionsPanel = ({ actions }) => (
  <div className="bg-white p-6 rounded-lg shadow">
    <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
    <div className="mt-4 grid grid-cols-1 gap-4">
      {actions.map((action) => (
        <Link
          key={action.to}
          to={action.to}
          className="flex items-center p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition"
        >
          {React.cloneElement(action.icon, { className: "h-6 w-6 text-[#2C4E86]" })}
          <span className="ml-3 font-medium text-gray-700">{action.label}</span>
        </Link>
      ))}
    </div>
  </div>
);

// --- DepartmentStats ---
const DepartmentStats = ({ departments = [] }) => (
  <div className="bg-white p-6 rounded-lg shadow">
    <h3 className="text-lg font-medium text-gray-900">Employees by Department</h3>
    <div className="mt-4 space-y-2">
      {departments.map(({ department, count }) => (
        <div key={department} className="flex justify-between items-center text-sm">
          <span className="text-gray-600 capitalize">{department}</span>
          <span className="font-semibold text-gray-800">{count}</span>
        </div>
      ))}
    </div>
  </div>
);

// --- DashboardPage ---
export default function DashboardPage() {
  const { user } = useAuth();
  const { data: analytics, isLoading } = useGetDashboardAnalytics();

  const hrActions = [
    { label: "Add New Employee", to: "/employee/create", icon: <UserPlus /> },
    { label: "Create New Tasks", to: "/task/create", icon: <ClipboardList /> },
    { label: "Log a New Function", to: "/demo/create", icon: <BarChart3 /> },
  ];

  const employeeActions = [
    { label: "View My Tasks", to: "/task/create", icon: <ClipboardList /> },
  ];

  if (isLoading) return <Loader />;

  return (
    <div className="w-full mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.name || "User"}!
        </h1>
        <p className="mt-1 text-gray-600">
          Here's a summary of what's happening today.
        </p>
      </div>

      {/* --- HR VIEW --- */}
      {(user?.role === "hr" || user?.role === "admin") && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <StatCard
              title="Total Employees"
              value={analytics?.employees}
              icon={<Users />}
              linkTo="/employee/list"
            />

            <GroupedStatCard
              title="Tasks Overview"
              icon={<NotebookText />}
              linkTo="/view-all-tasks"
              stats={[
                { label: "Total", value: analytics?.tasks?.total || 0 },
                { label: "Pending", value: analytics?.tasks?.pending || 0 },
                { label: "Completed", value: analytics?.tasks?.completed || 0 },

              ]}
            />

        

            <GroupedStatCard
              title="Expense Overview"
              icon={<IndianRupee />}
              linkTo="/expences/report"
              stats={[
                { label: "Total", value: analytics?.expenses?.total || 0 },
                { label: "Pending", value: analytics?.expenses?.pending || 0 },
                { label: "Completed", value: analytics?.expenses?.completed || 0 },
              ]}
            />

<GroupedStatCard
              title="Demos Overview "
              icon={<ClipboardListIcon />}
              linkTo="/demo/reports"
              stats={[
                { label: "Total", value: analytics?.demos?.total || 0 },
                { label: "Pending", value: analytics?.demos?.pending || 0 },
                { label: "Completed", value: analytics?.demos?.completed || 0 },
              ]}
            />

<GroupedStatCard
              title="Training Overview"
              icon={<NotepadText />}
              linkTo="/demo/reports"
              stats={[
                { label: "Total", value: analytics?.trainings?.total || 0 },
                { label: "Pending", value: analytics?.trainings?.pending || 0 },
                { label: "Completed", value: analytics?.trainings?.completed || 0 },
              ]}
            />

          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DepartmentStats departments={analytics?.byDepartment || []} />
            <QuickActionsPanel actions={hrActions} />
          </div>
        </>
      )}

      {/* --- EMPLOYEE VIEW --- */}
      {user?.role === "employee" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <GroupedStatCard
              title="My Tasks"
              icon={<ClipboardList />}
              linkTo="/view-all-tasks"
              stats={[
                { label: "Pending", value: analytics?.tasks?.pending || 0 },
                { label: "Completed", value: analytics?.tasks?.completed || 0 },
              ]}
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <QuickActionsPanel actions={employeeActions} />
          </div>
        </>
      )}
    </div>
  );
}
